import React from "react";
import { CheckCircle2, ShieldCheck, Layers, BarChart3, FileText, ArrowRight, Building2, Store, School, Fuel, Sparkles, ScrollText, MessageSquare, Lock, Key, Database, Network, Users, Zap, Mail, Landmark } from "lucide-react";
import { getLoginUrl } from '@/utils/domainUtils';
import Logo from './Logo';

// Complin Marketing Homepage
//
// LINKING TO LANDING PAGES:
// To link to Figma-generated landing pages, use:
//   <a href="/landing/hospitality">View Landing Page</a>
// Default: /landing/ (banking). Hospitality story: /landing/hospitality
// See marketing/README.md and marketing/INTEGRATION_GUIDE.md for details.

const HERO_HEADLINE = "Automate Mortgage Compliance Reviews — From Hours to Minutes";

const Feature = ({ icon: Icon, title, children }: { icon: React.ElementType; title: string; children: React.ReactNode }) => (
  <div className="rounded-2xl border bg-white shadow-sm p-6 hover:shadow-md transition-shadow">
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-xl bg-primary-50 border border-primary-100"><Icon className="w-5 h-5 text-primary-600"/></div>
      <div className="font-semibold text-gray-900">{title}</div>
    </div>
    <div className="mt-3 text-gray-600 text-sm leading-6">{children}</div>
  </div>
);

const Pill = ({ children, icon: Icon = CheckCircle2 }: { children: React.ReactNode; icon?: React.ElementType }) => (
  <span className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full border border-primary-100 bg-primary-50 text-primary-700">
    <Icon className="w-3 h-3 text-primary-600"/> {children}
  </span>
);

function Nav() {
  const handleSignIn = () => {
    window.location.href = getLoginUrl();
  };

  const handleGetStarted = () => {
    window.location.href = '/get-started';
  };

  return (
    <nav className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <Logo size="md" showText={true} />
        <div className="hidden md:flex items-center gap-6 text-sm">
          <a href="#features" className="text-gray-600 hover:text-black">Features</a>
          <a href="#how" className="text-gray-600 hover:text-black">How it works</a>
          <a href="/landing/" className="text-gray-600 hover:text-black">Mortgage compliance</a>
          <a href="#industries" className="text-gray-600 hover:text-black">Industries</a>
          <a href="#enterprise" className="text-gray-600 hover:text-black">Enterprise</a>
          <a href="#faq" className="text-gray-600 hover:text-black">FAQ</a>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSignIn} className="px-3 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm transition-colors">Sign in</button>
          <button onClick={handleGetStarted} className="px-3 py-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm transition-colors">Get Started</button>
        </div>
      </div>
    </nav>
  );
}

function Hero(){
  const handleStartFree = () => {
    window.location.href = getLoginUrl('signup=true');
  };

  const handleBookDemo = () => {
    window.location.href = '/get-started';
  };

  return (
    <header className="border-b bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-16 md:py-20 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full border border-primary-200 bg-primary-50 text-primary-700">
            <Landmark className="w-3 h-3 text-primary-600"/> Built for community &amp; mid-sized banks
          </div>
          <h1 className="text-3xl md:text-5xl font-semibold leading-tight mt-4 text-gray-900">{HERO_HEADLINE}</h1>
          <p className="mt-4 text-gray-600 leading-7">Complin.ai is the compliance automation platform for banks: automate up to 80% of mortgage compliance checks, target &gt;95% accuracy, and cut manual review time by 70–90%. LOS sync, rules + AI, and exception-driven workflows—audit-ready by design.</p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button onClick={handleBookDemo} className="px-4 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm flex items-center gap-2 transition-colors">Book a demo <ArrowRight className="w-4 h-4"/></button>
            <a href="/landing/#how-it-works" className="px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm transition-colors inline-flex items-center">See how it works</a>
            <button onClick={handleStartFree} className="px-4 py-2.5 rounded-xl border border-primary-200 text-primary-700 hover:bg-primary-50 text-sm transition-colors">Start free</button>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-gray-600">
            <Pill icon={ShieldCheck}>Exception-driven review</Pill>
            <Pill>Immutable audit logs</Pill>
            <Pill icon={ScrollText}>ECOA, Flood, HMDA checklists</Pill>
            <Pill>LOS ingestion</Pill>
          </div>
        </div>
        <div>
          <div className="rounded-2xl border bg-white shadow-sm p-4">
            <div className="rounded-xl border bg-gray-50 p-4">
              <div className="text-sm font-medium text-gray-900">Loan compliance snapshot</div>
              <div className="grid grid-cols-4 gap-2 mt-3 text-xs">
                {["HMDA","Flood","ECOA","TRID"].map((c, idx)=> {
                  // 80% green (3), 15% orange (0-1), 5% red (0-1)
                  // Distribution: 3 green, 1 orange
                  const percentages = [25, 22, 55, 30]; // All green (low risk)
                  const width = percentages[idx];
                  const getColor = (val: number, index: number) => {
                    // Make Facilities orange (15% of 4 = ~1 item)
                    if (index === 2) return 'bg-orange-500';
                    return 'bg-primary-600'; // Green for others (80%)
                  };
                  return (
                  <div key={idx} className="rounded-xl p-3 bg-white border">
                    <div className="text-gray-600 font-medium">{c}</div>
                    <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-2 ${getColor(width, idx)} rounded-full`} style={{width:`${width}%`}}/>
                    </div>
                  </div>
                  );
                })}
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                {Array.from({length:9}).map((_,i)=> {
                  // 80% green (7), 15% orange (1), 5% red (1)
                  // Distribution: 7 green, 1 orange, 1 red
                  const percentages = [28, 32, 25, 75, 22, 18, 35, 82, 30];
                  const width = percentages[i];
                  const getColor = (val: number, index: number) => {
                    // 1 red (5% of 9 = ~1 item) - Site 8
                    if (index === 7) return 'bg-red-600';
                    // 1 orange (15% of 9 = ~1 item) - Site 4
                    if (index === 3) return 'bg-orange-500';
                    // 7 green (80% of 9 = ~7 items)
                    return 'bg-primary-600';
                  };
                  return (
                  <div key={i} className="rounded-lg border bg-white p-3">
                    <div className="text-gray-600 font-medium">Loan {i+1}</div>
                      <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-1.5 ${getColor(width, i)} rounded-full`} style={{width:`${width}%`}}/>
                      </div>
                  </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="text-[10px] text-gray-400 mt-2">Mock data for illustration</div>
        </div>
      </div>
    </header>
  );
}

function Logos(){
  return (
    <section className="py-10 border-b">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-xs text-gray-500">Banks first — hospitality &amp; frontline teams still supported</div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-gray-400">
          <div className="rounded-xl border p-3 text-center">Community banks</div>
          <div className="rounded-xl border p-3 text-center">Credit unions</div>
          <a href="/landing/hospitality" className="rounded-xl border p-3 text-center text-primary-600 hover:bg-primary-50 transition-colors">Hotels &amp; lodging</a>
          <a href="/landing/hospitality" className="rounded-xl border p-3 text-center text-primary-600 hover:bg-primary-50 transition-colors">Retail &amp; QSR</a>
        </div>
      </div>
    </section>
  );
}

function Features(){
  return (
    <section id="features" className="py-16 border-b bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-semibold text-gray-900">Everything compliance-ready</h2>
          <p className="mt-3 text-gray-600">Centralize vendor contracts, licenses, inspections, and regulatory requirements — with automated reminders and audit-ready evidence.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          <Feature icon={ScrollText} title="Contract Management & Renewals Automation">
            Automatically track vendor contracts, permits, licenses, and service agreements. Get 90/60/30/7 day renewal reminders with automated escalation workflows to ensure nothing falls through the cracks.
          </Feature>
          <Feature icon={Sparkles} title="AI-Powered Document Extraction">
            Upload contracts, permits, or licenses. AI automatically extracts renewal dates, obligations, key terms, and proof requirements — eliminating manual data entry and reducing errors. <strong className="text-primary-600">Enterprise:</strong> Advanced field extraction with auto checklist completion.
          </Feature>
          <Feature icon={Layers} title="Compliance Templates Per Industry">
            Out‑of‑the‑box templates for Hotels, C-stores, Schools, and Retail. Each template includes risk tags, lead times, grace periods, and pass/fail criteria tailored to your industry.
          </Feature>
          <Feature icon={BarChart3} title="Compliance Scoring">
            See compliance status across all properties with real-time risk scoring. Identify overdue renewals, missing documents, vendor non-compliance, and locations at highest operational risk.
          </Feature>
          <Feature icon={ShieldCheck} title="Evidence Vault">
            Store all compliance documents in an audit-ready evidence vault. Every action is timestamped and documented. Export evidence and logs by site, category, and date range in one click. <strong className="text-primary-600">Enterprise:</strong> Immutable audit logging with 7-year retention.
          </Feature>
          <Feature icon={MessageSquare} title="AI Chat Agent for Compliance Q&A">
            Get instant answers to compliance questions. Ask about policies, requirements, deadlines, or best practices. AI-powered assistance helps your team stay compliant and informed.
          </Feature>
        </div>
        <div className="mt-8 text-center">
          <a href="#enterprise" className="text-primary-600 hover:text-primary-700 text-sm font-medium inline-flex items-center gap-1">
            View Enterprise Features <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

function How(){
  return (
    <section id="how" className="py-16 border-b bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-900">How it Works</h2>
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <div className="rounded-2xl border bg-white p-6 hover:shadow-md transition-shadow">
            <div className="text-sm text-primary-600 font-medium">Step 1</div>
            <div className="font-semibold mt-2 text-gray-900">Define Your Compliance Requirements</div>
            <p className="text-sm text-gray-600 mt-3">Use industry default templates or let AI help search for available compliance templates. Define all your organization compliance policies.</p>
          </div>
          <div className="rounded-2xl border bg-white p-6 hover:shadow-md transition-shadow">
            <div className="text-sm text-primary-600 font-medium">Step 2</div>
            <div className="font-semibold mt-2 text-gray-900">Assign Compliances to Locations</div>
            <p className="text-sm text-gray-600 mt-3">Assign compliances to locations. Notifications, escalations and follow-ups run automatically.</p>
          </div>
          <div className="rounded-2xl border bg-white p-6 hover:shadow-md transition-shadow">
            <div className="text-sm text-primary-600 font-medium">Step 3</div>
            <div className="font-semibold mt-2 text-gray-900">Collect Proof & Stay Audit-Ready</div>
            <p className="text-sm text-gray-600 mt-3">Upload service reports and evidence. AI identifies next due dates and exceptions, storing everything in an audit-ready vault.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Industries(){
  const industryLandingPages: Record<string, string> = {
    "Community & mid-sized banks": "/landing/",
    "Hotels & Lodging": "/landing/hospitality",
    "C‑Stores & Fuel": "/landing/hospitality",
    "Schools & Campuses": "/landing/hospitality",
    "Retail & QSR": "/landing/hospitality",
  };

  const cards = [
    { icon: Landmark, label: "Community & mid-sized banks", body: "Mortgage compliance automation: LOS sync, rules + AI, exception workflows, and audit-ready evidence." },
    { icon: Building2, label: "Hotels & Lodging", body: "Life safety, PM, licensing, and brand standards across rooms and back‑of‑house." },
    { icon: Fuel, label: "C‑Stores & Fuel", body: "Dispenser PM, spill kits, food handling, hood & duct, and tank inspections." },
    { icon: School, label: "Schools & Campuses", body: "Life safety drills, facilities checks, and ADA access compliance." },
    { icon: Store, label: "Retail & QSR", body: "Equipment PM, food safety, signage, and local permits in one place." }
  ];
  
  const handleIndustryClick = (label: string) => {
    const landingPage = industryLandingPages[label] || "/landing/";
    // Force a full page reload to bypass React Router
    // This ensures the landing page (served as static files) loads correctly
    window.location.href = landingPage;
  };

  return (
    <section id="industries" className="py-16 border-b bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-900">Built for banks — with hospitality &amp; frontline coverage</h2>
        <p className="mt-2 text-gray-600">Lead with mortgage and regulatory compliance; use the same platform for multi‑site operations where you need it.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mt-6">
          {cards.map((c,i)=> (
            <div 
              key={i} 
              onClick={() => handleIndustryClick(c.label)}
              className="rounded-2xl border bg-white p-6 hover:shadow-md transition-shadow cursor-pointer group"
            >
              <div className="p-2 rounded-xl bg-primary-50 border border-primary-100 inline-flex group-hover:bg-primary-100 transition-colors">
                <c.icon className="w-5 h-5 text-primary-600"/>
              </div>
              <div className="font-semibold mt-3 text-gray-900 group-hover:text-primary-600 transition-colors">{c.label}</div>
              <div className="text-sm text-gray-600 mt-2 leading-6">{c.body}</div>
              <div className="mt-4 text-xs text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                Learn more <ArrowRight className="w-3 h-3"/>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function EnterpriseFeatures(){
  const handleContactUs = () => {
    window.location.href = 'mailto:dc@complin.ai?subject=Enterprise Plan Inquiry';
  };

  return (
    <section id="enterprise" className="py-16 border-b bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full border border-primary-200 bg-primary-50 text-primary-700 mb-4">
            <ShieldCheck className="w-3 h-3 text-primary-600"/> Enterprise-Grade Security
          </div>
          <h2 className="text-2xl md:text-3xl font-semibold text-gray-900">Enterprise Features</h2>
          <p className="mt-3 text-gray-600 max-w-3xl mx-auto">
            Enterprise-grade security, advanced automation, and dedicated support for organizations that require the highest levels of compliance and data protection.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {/* SSO & Authentication */}
          <Feature icon={Users} title="Single Sign-On (SSO)">
            Enterprise SSO integration with Azure AD, Okta, and other identity providers. Multi-factor authentication (MFA) handled by your identity provider. Seamless user provisioning and deprovisioning.
          </Feature>

          {/* Auto Checklist Completion */}
          <Feature icon={Zap} title="AI-Powered Auto Checklist Completion">
            Automatically complete checklist items using AI document analysis. Extract field values from uploaded documents. Reduce manual data entry by up to 80% with intelligent field extraction.
          </Feature>

          {/* Field Extraction */}
          <Feature icon={FileText} title="Advanced Field Extraction">
            AI automatically extracts key information from contracts, permits, licenses, and compliance documents. Smart field mapping and validation. Supports custom field definitions per organization.
          </Feature>

          {/* Client-Side Encryption */}
          <Feature icon={Lock} title="Client-Side Encryption">
            Bank-grade envelope encryption (DEK/KEK). Documents encrypted before upload using AES-256-GCM. KMS never sees plaintext data. Per-organization encryption keys for complete isolation.
          </Feature>

          {/* Customer Managed Encryption Keys */}
          <Feature icon={Key} title="Customer Managed Encryption Keys (CMEK)">
            Full control over encryption keys. Per-organization Key Encryption Keys (KEKs). Automatic key rotation (90-365 days). Emergency key revocation capability. FIPS 140-2 Level 3 validated hardware.
          </Feature>

          {/* Multi-Layer Tenant Isolation */}
          <Feature icon={Database} title="Multi-Layer Tenant Isolation">
            Five layers of isolation: Storage (IAM-based buckets), KMS (per-org keys), Service Accounts (per-org accounts), Database (IAM conditions), and Application (defense in depth). Cryptographic separation ensures complete data isolation.
          </Feature>

          {/* VPC Service Controls */}
          <Feature icon={Network} title="VPC Service Controls & Private Endpoints">
            Private Service Connect endpoints for Vertex AI. VPC Service Controls perimeter. No public internet traversal for sensitive data. Network-level isolation and access controls.
          </Feature>

          {/* Immutable Audit Logging */}
          <Feature icon={ShieldCheck} title="Immutable Audit Logging">
            7-year audit log retention with retention lock. Immutable log storage prevents deletion even by admins. Comprehensive logging of all critical operations. GLBA-compliant audit trails.
          </Feature>

          {/* Security Monitoring */}
          <Feature icon={BarChart3} title="Advanced Security Monitoring">
            Real-time security alerts and anomaly detection. SIEM integration. Export monitoring and unusual activity detection. Failed login alerts and privilege escalation monitoring.
          </Feature>

          {/* Dedicated Infrastructure */}
          <Feature icon={Building2} title="Dedicated Infrastructure (Optional)">
            Option for dedicated Google Cloud project per organization. Complete infrastructure isolation. Custom data residency requirements. Dedicated support and SLAs.
          </Feature>

          {/* Compliance Certifications */}
          <Feature icon={CheckCircle2} title="GLBA Compliance Ready">
            GLBA Safeguards Rule compliant architecture. Comprehensive threat model and risk register. Documented security controls and mitigation mapping. Ready for bank vendor security reviews.
          </Feature>

          {/* Dedicated Support */}
          <Feature icon={Mail} title="Dedicated Enterprise Support">
            Priority support with dedicated account manager. Custom onboarding and training. SLA guarantees. Quarterly security reviews and compliance audits.
          </Feature>
        </div>

        <div className="mt-12 text-center">
          <button
            onClick={handleContactUs}
            className="px-6 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-base font-medium transition-colors inline-flex items-center gap-2"
          >
            <Mail className="w-5 h-5" />
            Contact Us for Enterprise Solution
          </button>
          <p className="mt-4 text-sm text-gray-600">
            Custom pricing available for enterprise deployments. All security features included.
          </p>
        </div>
      </div>
    </section>
  );
}

function FAQ(){
  const rows = [
    { q: "Do I need to replace my maintenance software?", a: "No. Complin.ai complements your existing maintenance software. Your maintenance tools handle work orders and repairs. Complin handles compliance, renewals, and audit readiness. They work together." },
    { q: "How does AI document extraction work?", a: "Upload contracts, permits, or licenses. AI automatically extracts renewal dates, obligations, and key information — eliminating manual data entry." },
    { q: "What if I don't have maintenance software?", a: "Complin includes lightweight asset and checklist features for organizations without maintenance software. But our primary focus is compliance lifecycle automation—which works whether you have maintenance tools or not." },
    { q: "How does this work with my facility management software?", a: "Facility management software tracks maintenance and operations. Complin tracks legal, regulatory, and contractual obligations—license renewals, vendor contracts, permit deadlines, audit evidence. Different problems, different solutions." },
    { q: "How do I pass audits with Complin?", a: "Every compliance action is timestamped and documented. Export evidence and logs by site, category, and date range in one click. Auditors love Complin because everything is verifiable and exportable." },
    { q: "What industries do you support?", a: "Complin is built for multi-site operations across Hotels, C-stores, Schools, and Retail. Industry-specific templates and workflows included." }
  ];
  return (
    <section id="faq" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-900">Frequently asked questions</h2>
        <p className="mt-2 text-gray-600">Everything you need to know about automated compliance management.</p>
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          {rows.map((r,i)=> (
            <div key={i} className="rounded-2xl border bg-white p-6 hover:shadow-md transition-shadow">
              <div className="font-medium text-gray-900">{r.q}</div>
              <div className="text-sm text-gray-600 mt-2 leading-6">{r.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function MarketingPage(){
  return (
    <div className="min-h-screen bg-white text-black">
      <Nav />
      <Hero />
      <Features />
      <How />
      <Industries />
      <EnterpriseFeatures />
      <FAQ />
      <footer className="border-t">
        <div className="max-w-7xl mx-auto px-4 py-8 text-sm text-gray-500 flex items-center justify-between">
          <div>© {new Date().getFullYear()} Complin, Inc.</div>
          <div className="flex items-center gap-4">
            <a href="/privacy" className="hover:text-black">Privacy</a>
            <a href="/terms" className="hover:text-black">Terms</a>
            <a href="/security" className="hover:text-black">Security</a>
            {/* Example: Link to landing pages */}
            {/* <a href="/landing/hospitality" className="hover:text-black">Hospitality Landing Page</a> */}
          </div>
        </div>
      </footer>
    </div>
  );
}

